import { useMemo, useState } from "react";
import { createContainer } from "unstated-next";
import { useHistogramData } from "../../hooks/useHistogram";
import { getCategoryName, getTagName, TAGS, ALL_CATEGORIES } from "../../constants/analyticsConstants/generativeCallReview";
import { ocean_blue, voicify_spark_dark, voicify_spark_medium } from "../../constants/colors";

export type HistogramView = "Containable Conversations" | "Spanish Calls" | "All Conversations" | "Average Calls by Hour" | "Average Calls by Day of Week" | "Concurrent Calls";

export type HistogramParams = {
    applicationId?: string; // Optional for single app mode
    applicationIds?: string[]; // For organization-level (multiple apps)
    startDate: string;   // local naive, e.g. "2025-09-17 11:59 pm" or "YYYY-MM-DD HH:mm"
    endDate: string;     // local naive
    categories: string[];
    tags: string[];
    featureFlagId?: string;
    title?: string; // Optional custom title
    titleStyles?: React.CSSProperties; // Optional custom title styles
    subtitle?: string; // Optional custom subtitle
    scale?: number; // Optional scale for zoom control (e.g., 0.9 to zoom out 10%)
    selectedView?: HistogramView; // Selected view for multi-line charts
};

type InitialState = Partial<HistogramParams>;

function useHistogramContainer(initialState: InitialState = {}) {
    // --- Params state the app can update from outside (mirrors your "container" style)
    const [params, setParams] = useState<HistogramParams>({
        applicationId: initialState.applicationId,
        applicationIds: initialState.applicationIds ?? [],
        startDate: initialState.startDate ?? "",
        endDate: initialState.endDate ?? "",
        categories: initialState.categories ?? [],
        tags: initialState.tags ?? [],
        featureFlagId: initialState.featureFlagId,
        title: initialState.title,
        titleStyles: initialState.titleStyles,
        subtitle: initialState.subtitle,
        scale: initialState.scale,
        selectedView: initialState.selectedView ?? "All Conversations",
    });

    // --- Helpers
    const splitDateString = (dateTime: string, isUtc: boolean) => {
        const parts = isUtc ? dateTime.split("T") : dateTime.split(" ");
        const date = parts[0];
        const time = isUtc
            ? parts[1].split(":")[0] + ":" + parts[1].split(":")[1]
            : parts[1] + (parts.length > 2 ? parts[2] : "");
        const [y, m, d] = date.split("-");
        return [y, m, d, time] as const;
    };

    const MONTH_MAP = new Map<number, string>([
        [1, "Jan."], [2, "Feb."], [3, "Mar."], [4, "Apr."], [5, "May."], [6, "Jun."],
        [7, "Jul."], [8, "Aug."], [9, "Sep."], [10, "Oct."], [11, "Nov."], [12, "Dec."]
    ]);

    // --- Derived inputs
    const sdSplit = useMemo(() => (params.startDate ? splitDateString(params.startDate, false) : ["", "", "", ""] as const), [params.startDate]);
    const edSplit = useMemo(() => (params.endDate ? splitDateString(params.endDate, false) : ["", "", "", ""] as const), [params.endDate]);

    const interval = useMemo(() => {
        const sameDay = sdSplit[0] && edSplit[0] &&
            sdSplit[0] === edSplit[0] && sdSplit[1] === edSplit[1] && sdSplit[2] === edSplit[2];
        return sameDay ? 2 : 3;
    }, [sdSplit, edSplit]);

    const toIsoString = useMemo(() => (dateTimeStr: string): string => {
        if (!dateTimeStr) return "";
        const [date, time, meridiem] = dateTimeStr.split(" ");
        let [hours, minutes] = time.split(":");

        // Convert to 24-hour format
        let hour = parseInt(hours);
        if (meridiem?.toLowerCase() === "pm" && hour !== 12) hour += 12;
        if (meridiem?.toLowerCase() === "am" && hour === 12) hour = 0;

        // Pad with leading zeros
        const formattedHour = hour.toString().padStart(2, "0");
        return `${date}T${formattedHour}:${minutes}:00`;
    }, []);

    const startDateForApi = useMemo(() => toIsoString(params.startDate), [params.startDate]);
    const endDateForApi = useMemo(() => toIsoString(params.endDate), [params.endDate]);

    // Check if we're using custom categories/tags (old implementation) or multi-view mode (new dropdown)
    const useCustomCategoriesAndTags = params.categories && params.categories.length > 0;

    // Define categories and tags for each view
    const viewConfig = useMemo(() => {
        const containableCategories = ["ACTION_ATTEMPTED", "OTHER"];

        switch (params.selectedView) {
            case "Containable Conversations":
                return {
                    datasets: [
                        { name: "Containable Conversations", categories: containableCategories, tags: [], color: voicify_spark_dark },
                        { name: "Contained Conversations", categories: containableCategories, tags: [TAGS.ACTION_COMPLETED], color: ocean_blue }
                    ]
                };
            case "Spanish Calls":
                return {
                    datasets: [
                        { name: "All Containable Conversations", categories: containableCategories, tags: [], color: voicify_spark_dark },
                        { name: "Spanish Containable Conversations", categories: containableCategories, tags: [TAGS.SPANISH], color: voicify_spark_medium },
                        { name: "Spanish Contained Conversations", categories: containableCategories, tags: [TAGS.ACTION_COMPLETED, TAGS.SPANISH], color: ocean_blue }
                    ]
                };
            case "All Conversations":
                return {
                    datasets: [
                        { name: "All Conversations", categories: ALL_CATEGORIES, tags: [], color: voicify_spark_dark },
                        { name: "Containable Conversations", categories: containableCategories, tags: [], color: voicify_spark_medium },
                        { name: "Contained Conversations", categories: containableCategories, tags: [TAGS.ACTION_COMPLETED], color: ocean_blue }
                    ]
                };
            default:
                return {
                    datasets: [
                        { name: "Containable Conversations", categories: containableCategories, tags: [], color: voicify_spark_dark },
                        { name: "Contained Conversations", categories: containableCategories, tags: [TAGS.ACTION_COMPLETED], color: voicify_spark_medium }
                    ]
                };
        }
    }, [params.selectedView]);

    // Fetch data for the first dataset (primary)
    const { data: data1, loading: loading1, error: error1 } = useHistogramData({
        applicationId: params.applicationId,
        applicationIds: params.applicationIds,
        startDate: startDateForApi,
        endDate: endDateForApi,
        categories: useCustomCategoriesAndTags ? params.categories : (viewConfig.datasets[0]?.categories || []),
        tags: useCustomCategoriesAndTags ? params.tags : (viewConfig.datasets[0]?.tags || []),
        interval
    });

    // Fetch data for the second dataset (only for multi-view mode)
    const { data: data2, loading: loading2, error: error2 } = useHistogramData({
        applicationId: params.applicationId,
        applicationIds: params.applicationIds,
        startDate: startDateForApi,
        endDate: endDateForApi,
        categories: useCustomCategoriesAndTags ? [] : (viewConfig.datasets[1]?.categories || []),
        tags: useCustomCategoriesAndTags ? [] : (viewConfig.datasets[1]?.tags || []),
        interval
    });

    // Fetch data for the third dataset (only for Spanish Calls and All Conversations views in multi-view mode)
    const { data: data3, loading: loading3, error: error3 } = useHistogramData({
        applicationId: params.applicationId,
        applicationIds: params.applicationIds,
        startDate: startDateForApi,
        endDate: endDateForApi,
        categories: useCustomCategoriesAndTags ? [] : (viewConfig.datasets[2]?.categories || []),
        tags: useCustomCategoriesAndTags ? [] : (viewConfig.datasets[2]?.tags || []),
        interval
    });

    const loading = loading1 || loading2 || loading3;
    const error = error1 || error2 || error3;

    const compareDates = (date1: string, date2: string): number => {
        if (!date1 || !date2) return 0;
        const time1 = new Date(date1).getTime();
        const time2 = new Date(date2).getTime();
        if (time1 > time2) return 1;
        if (time1 < time2) return -1;
        return 0;
    };


    // --- Derived presentation values
    const { title, subtitle, mappedDatasets } = useMemo(() => {

        const mapDay = (day: string, month: string) => `${MONTH_MAP.get(Number(month))} ${day}`;

        const formattedStart = sdSplit[0] ? mapDay(sdSplit[2], sdSplit[1]) : "";
        const formattedEnd = edSplit[0] ? mapDay(edSplit[2], edSplit[1]) : "";

        // Use custom title if provided, otherwise use selectedView only if explicitly set, otherwise default
        const title = params.title ?? (params.selectedView && params.selectedView !== "Containable Conversations" ? params.selectedView : "CALL VOLUME");
        let subtitle = params.subtitle ?? (
            formattedStart && formattedEnd
                ? (formattedStart === formattedEnd ? formattedStart : `${formattedStart}-${formattedEnd}`)
                : ""
        );

        const dateForAxis = (isoLocal: string) => {
            const time = isoLocal;

            // Get actual JS Date
            const dateObj = new Date(time);

            // Day-of-week map → first letter
            const dayLetterMap = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
            const dayLetter = dayLetterMap[dateObj.getDay()]; // 0 = Sunday

            const [, m, d, hhmm] = splitDateString(time, true);

            if (interval >= 3) {
                // Example: 11/23 (M)
                return `${m}/${d} (${dayLetter})`;
            } else {
                let hour = Number(hhmm.split(":")[0]);
                const minute = hhmm.split(":")[1];
                const ampm = hour >= 12 ? "PM" : "AM";
                hour = hour % 12 || 12;

                // Example: 3:45 PM (T)
                return `${hour}:${minute} ${ampm} (${dayLetter})`;
            }
        };

        const cleanAndMapData = (rawData: any[]) => {
            let cleanData = []
            if (rawData?.length > 0 && rawData !== undefined && rawData !== null) {
                cleanData = rawData.map(datum => {
                    //If the datum date is less than the startdate or greater than the endDate on the props and the count is 0, don't return it
                    if ((compareDates(datum.date, startDateForApi) < 0 || compareDates(datum.date, endDateForApi) > 0) && datum.count === 0) return null;
                    return datum;
                }).filter(Boolean);
            }

            return (cleanData ?? []).map((d: any) => ({
                x: dateForAxis(d.date),
                y: d.count,
                fullDate: d.date,
            }));
        };

        const mappedDatasets = [];

        if (useCustomCategoriesAndTags) {
            // Old implementation - single dataset with custom categories/tags
            mappedDatasets.push({
                id: "Volume",
                data: cleanAndMapData(data1),
                color: ocean_blue
            });
        } else {
            // New implementation - multiple datasets based on viewConfig
            mappedDatasets.push({
                id: viewConfig.datasets[0]?.name || "Line 1",
                data: cleanAndMapData(data1),
                color: viewConfig.datasets[0]?.color || ocean_blue
            });

            if (viewConfig.datasets[1]) {
                mappedDatasets.push({
                    id: viewConfig.datasets[1].name,
                    data: cleanAndMapData(data2),
                    color: viewConfig.datasets[1].color
                });
            }

            if (viewConfig.datasets[2] && data3) {
                mappedDatasets.push({
                    id: viewConfig.datasets[2].name,
                    data: cleanAndMapData(data3),
                    color: viewConfig.datasets[2].color
                });
            }
        }

        return { title, subtitle, mappedDatasets };
    }, [data1, data2, data3, interval, sdSplit, edSplit, params.selectedView, viewConfig, startDateForApi, endDateForApi, useCustomCategoriesAndTags]);

    // For backward compatibility, provide mappedData as the first dataset's data
    const mappedData = mappedDatasets[0]?.data || [];

    return {
        params,
        setParams,            // (updater: (prev: HistogramParams) => HistogramParams) => void
        title,
        titleStyles: params.titleStyles,
        subtitle,
        mappedData,           // Backward compatible - single line data
        mappedDatasets,       // Multi-line data for new implementation
        selectedView: params.selectedView,
        setSelectedView: (view: HistogramView) => setParams(prev => ({ ...prev, selectedView: view })),
        loading,
        error,
        scale: params.scale
    };
}

const HistogramContainer = createContainer(useHistogramContainer);
export default HistogramContainer;